<!--
  AdminSecurity.svelte — Security & access-control configuration page.

  Allows admins to manage:
    - IP whitelisting / blacklisting
    - Token refresh period
    - Password reset attempt limits
    - Verification link expiry durations
    - Duplicate email policy

  Loads the singleton Settings row on mount and saves via `updateSettings()`.
-->
<script lang="ts">
	import { Button, Input, Label, Toggle, Heading, Spinner, Helper } from 'flowbite-svelte';
	import AbstractCard from '../../components/AbstractCard.svelte';
	import { onMount } from 'svelte';
	import { getSettings, updateSettings } from '../../AppNetwork';
	import type { Settings } from '../../AppTypes';
	import { toastSuccess, toastError } from '../../toast';
	import { t, t2 } from '../../i18n';

	/** Current settings loaded from server. */
	let settings: Settings | null = null;
	/** Whether the initial load is in progress. */
	let loading = $state(true);
	/** Whether a save operation is in progress. */
	let saving = $state(false);
	/** Error message from the last operation. */
	let error = $state('');

	// ── Form fields ──────────────────────────────────────────────

	/** Comma-separated CIDR whitelist. `0.0.0.0/0` = allow all. */
	let whitelistedIPs = $state('');
	/** Comma-separated IP blacklist. */
	let blacklistedIPs = $state('');
	/** Seconds before expiry during which token refresh is allowed. */
	let renewalRefreshTokenPeriodInSeconds = $state(4320);
	/** Max password-reset attempts before lockout (empty = unlimited). */
	let maxResetPasswordAttempts: string = $state('');
	/** Max forgot-password requests before lockout (empty = unlimited). */
	let maxForgotPasswordAttempts: string = $state('');
	/** Duration string for user activation email link expiry. */
	let userActivationMailExpiry = $state('');
	/** Duration string for forgot-password link expiry. */
	let forgotPasswordVerificationExpiry = $state('');
	/** Duration string for reset-password link expiry. */
	let resetPasswordVerificationExpiry = $state('');
	/** Whether multiple users may share the same email address. */
	let enableDuplicateEmails = $state(false);

	/**
	 * Load the current settings from the server and populate the form.
	 */
	onMount(async () => {
		try {
			settings = await getSettings();
			if (settings) populateForm(settings);
			else error = t2('No settings found. Save defaults to create the settings row.');
		} catch (err) {
			error = err instanceof Error ? err.message : t2('Failed to load settings');
		} finally {
			loading = false;
		}
	});

	/**
	 * Populate security form fields from a Settings object.
	 * @param s - The settings object to read from.
	 */
	function populateForm(s: Settings): void {
		whitelistedIPs = s.whitelistedIPs || '';
		blacklistedIPs = s.blacklistedIPs || '';
		renewalRefreshTokenPeriodInSeconds = s.renewalRefreshTokenPeriodInSeconds;
		maxResetPasswordAttempts = s.maxResetPasswordAttempts?.toString() || '';
		maxForgotPasswordAttempts = s.maxForgotPasswordAttempts?.toString() || '';
		userActivationMailExpiry = s.userActivationMailExpiry || '';
		forgotPasswordVerificationExpiry = s.forgotPasswordVerificationExpiry || '';
		resetPasswordVerificationExpiry = s.resetPasswordVerificationExpiry || '';
		enableDuplicateEmails = s.enableDuplicateEmails;
	}

	/**
	 * Save the current security settings to the server.
	 */
	async function handleSave(): Promise<void> {
		saving = true;
		error = '';

		/** Send updated security settings to the server and refresh the form. */
		try {
			settings = await updateSettings({
				whitelistedIPs: whitelistedIPs || '0.0.0.0/0',
				blacklistedIPs: blacklistedIPs || null,
				renewalRefreshTokenPeriodInSeconds,
				maxResetPasswordAttempts: maxResetPasswordAttempts ? parseInt(maxResetPasswordAttempts) : null,
				maxForgotPasswordAttempts: maxForgotPasswordAttempts ? parseInt(maxForgotPasswordAttempts) : null,
				userActivationMailExpiry: userActivationMailExpiry || null,
				forgotPasswordVerificationExpiry: forgotPasswordVerificationExpiry || null,
				resetPasswordVerificationExpiry: resetPasswordVerificationExpiry || null,
				enableDuplicateEmails
			});
			populateForm(settings);
			toastSuccess(t2('Security settings saved successfully.'));
		} catch (err) {
			toastError(err instanceof Error ? err.message : t2('Failed to save security settings'));
		} finally {
			saving = false;
		}
	}
</script>

<Heading tag="h3">{$t("Security & Access Control")}</Heading>

{#if loading}
	<div class="flex items-center justify-center py-12">
		<Spinner size="8" />
	</div>
{:else}
	<div class="mt-4 space-y-6">
		{#if error}
			<div class="rounded-lg bg-red-50 p-4 text-red-700 dark:bg-red-900/20 dark:text-red-300">
				<i class="fa-solid fa-exclamation-triangle me-2"></i>{error}
			</div>
		{/if}

		<!-- IP Access Control -->
		<AbstractCard class="max-w-none">
			<h4 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
				<i class="fa-solid fa-shield-halved me-2"></i>{$t("IP Access Control")}
			</h4>

			<div class="grid grid-cols-1 gap-4 md:grid-cols-2">
				<div>
					<Label for="whitelistedIPs" class="mb-2">{$t("Whitelisted IPs")}</Label>
					<Input id="whitelistedIPs" bind:value={whitelistedIPs} placeholder="0.0.0.0/0" />
					<Helper class="mt-1">{$t("CIDR notation, comma-separated.")} <code>0.0.0.0/0</code> = {$t("allow all.")}</Helper>
				</div>

				<div>
					<Label for="blacklistedIPs" class="mb-2">{$t("Blacklisted IPs")}</Label>
					<Input id="blacklistedIPs" bind:value={blacklistedIPs} placeholder="192.168.1.1, 10.0.0.0/8" />
					<Helper class="mt-1">{$t("Comma-separated. Evaluated after whitelist.")}</Helper>
				</div>
			</div>
		</AbstractCard>

		<!-- Token & Session -->
		<AbstractCard class="max-w-none">
			<h4 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
				<i class="fa-solid fa-clock me-2"></i>{$t("Token & Session")}
			</h4>

			<div class="grid grid-cols-1 gap-4 md:grid-cols-2">
				<div>
					<Label for="renewalPeriod" class="mb-2">{$t("Token Refresh Window (seconds)")}</Label>
					<Input
						id="renewalPeriod"
						type="number"
						bind:value={renewalRefreshTokenPeriodInSeconds}
						placeholder="4320"
					/>
					<Helper class="mt-1"
						>{$t("Seconds before expiry during which a refresh is allowed. Default 4320 (72 min).")}</Helper
					>
				</div>
			</div>
		</AbstractCard>

		<!-- Rate Limiting -->
		<AbstractCard class="max-w-none">
			<h4 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
				<i class="fa-solid fa-gauge-high me-2"></i>{$t("Rate Limiting")}
			</h4>

			<div class="grid grid-cols-1 gap-4 md:grid-cols-2">
				<div>
					<Label for="maxResetPasswordAttempts" class="mb-2">{$t("Max Reset-Password Attempts")}</Label>
					<Input
						id="maxResetPasswordAttempts"
						type="number"
						bind:value={maxResetPasswordAttempts}
						placeholder={$t("Unlimited")}
					/>
					<Helper class="mt-1">{$t("Leave empty for unlimited.")}</Helper>
				</div>

				<div>
					<Label for="maxForgotPasswordAttempts" class="mb-2">{$t("Max Forgot-Password Requests")}</Label>
					<Input
						id="maxForgotPasswordAttempts"
						type="number"
						bind:value={maxForgotPasswordAttempts}
						placeholder={$t("Unlimited")}
					/>
					<Helper class="mt-1">{$t("Leave empty for unlimited.")}</Helper>
				</div>
			</div>
		</AbstractCard>

		<!-- Verification Link Expiry -->
		<AbstractCard class="max-w-none">
			<h4 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
				<i class="fa-solid fa-hourglass-half me-2"></i>{$t("Verification Link Expiry")}
			</h4>

			<div class="grid grid-cols-1 gap-4 md:grid-cols-3">
				<div>
					<Label for="userActivationMailExpiry" class="mb-2">{$t("Activation Email")}</Label>
					<Input id="userActivationMailExpiry" bind:value={userActivationMailExpiry} placeholder="24h" />
					<Helper class="mt-1">e.g. <code>24h</code>, <code>7d</code></Helper>
				</div>

				<div>
					<Label for="forgotPasswordVerificationExpiry" class="mb-2">{$t("Forgot Password")}</Label>
					<Input
						id="forgotPasswordVerificationExpiry"
						bind:value={forgotPasswordVerificationExpiry}
						placeholder="1h"
					/>
				</div>

				<div>
					<Label for="resetPasswordVerificationExpiry" class="mb-2">{$t("Reset Password")}</Label>
					<Input
						id="resetPasswordVerificationExpiry"
						bind:value={resetPasswordVerificationExpiry}
						placeholder="1h"
					/>
				</div>
			</div>
		</AbstractCard>

		<!-- Policies -->
		<AbstractCard class="max-w-none">
			<h4 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
				<i class="fa-solid fa-list-check me-2"></i>{$t("Policies")}
			</h4>

			<div class="flex items-center gap-3">
				<Toggle bind:checked={enableDuplicateEmails} />
				<div>
					<p class="font-medium text-gray-900 dark:text-white">{$t("Allow Duplicate Emails")}</p>
					<p class="text-sm text-gray-500 dark:text-gray-400">
						{$t("When enabled, multiple accounts may share the same email address.")}
					</p>
				</div>
			</div>
		</AbstractCard>

		<!-- Save -->
		<div class="flex justify-end">
			<Button onclick={handleSave} disabled={saving}>
				{#if saving}
					<i class="fa-solid fa-spinner fa-spin me-2"></i>{$t("Saving...")}
				{:else}
					<i class="fa-solid fa-save me-2"></i>{$t("Save Security Settings")}
				{/if}
			</Button>
		</div>
	</div>
{/if}
